import React from "react";
import moment from "moment";
import { flatMap, each, memoize } from "lodash";
import FileUploadField from "components/shared/form/fields/FileUploadField";
import { useSendTestBirthdayEmail } from "./api";
import CheckboxFieldWrapper from "components/shared/form/fields/CheckboxFieldWrapper";
import Checkbox from "components/shared/form/fields/Checkbox";
import Select from "components/shared/form/fields/Select";
import NumberField from "components/shared/form/fields/NumberFiield";
import RadioButton from "components/shared/form/fields/RadioButton";
import { useFormContext } from "react-hook-form";
import TextInput from "components/shared/form/fields/TextInput";
import TextEditor from "components/shared/form/fields/TextEditor";

type day = { name: string; index: number };

export const createDaysArray: () => day[] = memoize(() => {
  const days: Array<day> = [];
  each(moment.localeData().weekdays(), (weekday, index) =>
    days.push({ name: weekday, index: index }),
  );

  let moveArray = moment.localeData().firstDayOfWeek();
  while (moveArray != 0) {
    if (days.length) {
      days.push(days.shift() as day);
    }
    moveArray--;
  }

  return days;
});

function MailSettings() {
  const { watch } = useFormContext();
  const digests_daily_activity_enabled = watch("daily_activity.enabled");
  const birthday_emails_enabled = watch("birthday_emails.enabled");

  const birthday_emails = watch("birthday_emails");

  const { mutateAsync: sendTestMail } = useSendTestBirthdayEmail({
    onSuccess: () => {
      toastr.success("Test Mail erfolgreich gesendet");
    },
  });

  function submitTestMail(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();

    const params = {
      birthday_emails: {
        ...birthday_emails,
        image: birthday_emails.image[0] ?? birthday_emails.image,
      },
    };

    sendTestMail(params);
  }

  const days = createDaysArray();
  return (
    <>
      <h3>
        {I18n.t("js.administration.network_settings.mail_settings.title")}
      </h3>
      <div className="form-horizontal flex flex-col gap-3">
        <CheckboxFieldWrapper
          label={I18n.t("js.networks.notifications.label")}
          hint={I18n.t("js.networks.notifications.dm_body.help")}
        >
          <Checkbox
            name="notifications_send_direct_message_body"
            label={I18n.t("js.networks.notifications.dm_body.label")}
          />
        </CheckboxFieldWrapper>
        {Preload.current_network.config.email_recommendations
          .allow_email_recommendation_configuration_for_network_admins && (
          <CheckboxFieldWrapper
            label={I18n.t(
              "js.administration.network_settings.activity_recommendation_email.label",
            )}
            hint={I18n.t(
              "js.administration.network_settings.activity_recommendation_email.help",
            )}
          >
            <Checkbox
              name="email_recommendations.allow_external_recipients"
              label={I18n.t(
                "js.administration.network_settings.activity_recommendation_email.checkbox",
              )}
            />
            <Checkbox
              name="email_recommendations.include_replies_default"
              label={I18n.t(
                "js.activitystreams.email_recommendations.include_replies_label",
              )}
            />
          </CheckboxFieldWrapper>
        )}
        {Preload.current_network.config.digests.enabled && (
          <>
            <h4 className="font-bold">
              {I18n.t("js.administration.network_settings.digests.label")}
            </h4>
            <Select
              name="digest_sending_days"
              label={I18n.t(
                "js.administration.network_settings.digest_sending_days.label",
              )}
              hint={
                Preload.current_network.config.digests.configurable
                  ? undefined
                  : I18n.t(
                      "js.administration.network_settings.setting_restricted_by_plan",
                    )
              }
              options={flatMap(days, (day) => [
                {
                  key: `${day.index}_am`,
                  value: { day: day.index, time: "am" },
                  label: `${day.name} ${I18n.t(
                    "js.administration.network_settings.digests.am",
                  )}`,
                },
                {
                  key: `${day.index}_pm`,
                  value: { day: day.index, time: "pm" },
                  label: `${day.name} ${I18n.t(
                    "js.administration.network_settings.digests.pm",
                  )}`,
                },
                {
                  key: `${day.index}_lpm`,
                  value: { day: day.index, time: "lpm" },
                  label: `${day.name} ${I18n.t(
                    "js.administration.network_settings.digests.lpm",
                  )}`,
                },
              ])}
              multi
            />
            {Preload.current_network.config.digests.configurable && (
              <>
                <NumberField
                  name="digests.max_number_of_upcoming_appointments"
                  label={I18n.t(
                    "js.administration.network_settings.max_number_of_upcoming_appointments.label",
                  )}
                  max={30}
                  min={0}
                  required
                />
                <RadioButton
                  name="digests.upcoming_appointments_position"
                  label={I18n.t(
                    "js.administration.network_settings.upcoming_appointments_position.label",
                  )}
                  options={[
                    {
                      value: "top",
                      label: I18n.t(
                        "js.administration.network_settings.upcoming_appointments_position.top",
                      ),
                    },
                    {
                      value: "bottom",
                      label: I18n.t(
                        "js.administration.network_settings.upcoming_appointments_position.bottom",
                      ),
                    },
                  ]}
                />
                <RadioButton
                  name="digests.show_comments"
                  label={I18n.t(
                    "js.administration.network_settings.show_comments.label",
                  )}
                  options={[
                    {
                      value: "true",
                      label: I18n.t(
                        "js.administration.network_settings.show_comments.show",
                      ),
                    },
                    {
                      value: "false",
                      label: I18n.t(
                        "js.administration.network_settings.show_comments.show_not",
                      ),
                    },
                  ]}
                />
              </>
            )}
          </>
        )}
        {Preload.current_network.config.digests.daily_activity
          .feature_available && (
          <CheckboxFieldWrapper
            label={I18n.t(
              "js.administration.network_settings.daily_digests.label",
            )}
          >
            <div className="flex flex-col gap-4 divide-y divide-neutral">
              <RadioButton
                name="daily_activity.enabled"
                options={[
                  {
                    value: "true",
                    label: I18n.t(
                      "js.administration.network_settings.daily_digests.enabled.true",
                    ),
                  },
                  {
                    value: "false",
                    label: I18n.t(
                      "js.administration.network_settings.daily_digests.enabled.false",
                    ),
                  },
                ]}
                hint={I18n.t(
                  "js.administration.network_settings.daily_digests.enabled.help",
                )}
                withoutWrapper
              />
              {digests_daily_activity_enabled === "true" && (
                <>
                  <RadioButton
                    name="daily_activity.include_network_level"
                    label={I18n.t(
                      "js.administration.network_settings.daily_digests.include_network_level.label",
                    )}
                    options={[
                      {
                        value: "true",
                        label: I18n.t(
                          "js.administration.network_settings.daily_digests.include_network_level.true",
                        ),
                      },
                      {
                        value: "false",
                        label: I18n.t(
                          "js.administration.network_settings.daily_digests.include_network_level.false",
                        ),
                      },
                    ]}
                    withoutWrapper
                  />
                  <RadioButton
                    name="daily_activity.mode"
                    label={I18n.t(
                      "js.administration.network_settings.daily_digests.mode.label",
                    )}
                    options={[
                      {
                        value: "new",
                        label: I18n.t(
                          "js.administration.network_settings.daily_digests.mode.new",
                        ),
                      },
                      {
                        value: "new_and_updated",
                        label: I18n.t(
                          "js.administration.network_settings.daily_digests.mode.new_and_updated",
                        ),
                      },
                    ]}
                    withoutWrapper
                  />
                  <RadioButton
                    name="daily_activity.sending_time"
                    label={I18n.t(
                      "js.administration.network_settings.daily_digests.sending_time.label",
                    )}
                    options={[
                      {
                        value: "am",
                        label: I18n.t(
                          "js.administration.network_settings.daily_digests.sending_time.am",
                        ),
                      },
                      {
                        value: "pm",
                        label: I18n.t(
                          "js.administration.network_settings.daily_digests.sending_time.pm",
                        ),
                      },
                      {
                        value: "lpm",
                        label: I18n.t(
                          "js.administration.network_settings.daily_digests.sending_time.lpm",
                        ),
                      },
                    ]}
                    withoutWrapper
                  />
                  <RadioButton
                    name="daily_activity.only_weekdays"
                    label={I18n.t(
                      "js.administration.network_settings.daily_digests.only_weekdays.label",
                    )}
                    options={[
                      {
                        value: "true",
                        label: I18n.t(
                          "js.administration.network_settings.daily_digests.only_weekdays.true",
                        ),
                      },
                      {
                        value: "false",
                        label: I18n.t(
                          "js.administration.network_settings.daily_digests.only_weekdays.false",
                        ),
                      },
                    ]}
                    withoutWrapper
                  />
                </>
              )}
            </div>
          </CheckboxFieldWrapper>
        )}
        {Preload.current_network.config.birthday_emails.admin_configurable && (
          <div className="flex flex-col gap-4">
            <h4 className="font-bold">
              {I18n.t(
                "js.administration.network_setting.birthday_emails.title",
              )}
            </h4>
            <RadioButton
              name="birthday_emails.enabled"
              label={I18n.t(
                "js.administration.network_setting.birthday_emails.enable_label",
              )}
              options={[
                {
                  value: "true",
                  label: I18n.t(
                    "js.administration.network_setting.choices.yes",
                  ),
                },
                {
                  value: "false",
                  label: I18n.t("js.administration.network_setting.choices.no"),
                },
              ]}
            />
            {birthday_emails_enabled === "true" && (
              <>
                <FileUploadField
                  name="birthday_emails.image"
                  label={I18n.t(
                    "js.administration.network_setting.birthday_emails.image_label",
                  )}
                  options={{
                    isImage: true,
                    multiple: false,
                    storageDirectory: "assets",
                  }}
                  hint={I18n.t(
                    "js.administration.network_setting.birthday_emails.image_size_hint",
                  )}
                />
                <TextInput
                  name="birthday_emails.subject"
                  label={I18n.t(
                    "js.administration.network_setting.birthday_emails.subject_label",
                  )}
                />
                <TextEditor
                  name="birthday_emails.text"
                  label={I18n.t(
                    "js.administration.network_setting.birthday_emails.text_label",
                  )}
                />
                <div className="flex justify-end mt-4">
                  <button className="btn btn-primary" onClick={submitTestMail}>
                    {I18n.t(
                      "js.administration.network_setting.birthday_emails.send_test_mail_label",
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default MailSettings;
